/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { TableRow, TableCell, IconButton } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/system';
import {
  BUTTON_ICON_STYLE,
  EXPAND_BUTTON_BOX_STYLE,
  EXPAND_BUTTON_STYLE,
  ROW_STYLE,
} from './style';

export const BodyRow = ({
  row,
  rowIndex,
  flatColumns,
  expandedRowIds,
  setExpandedRowIds,
  primaryRowColor,
  secondaryRowColor,
  primaryTextColor,
  secondaryTextColor,
  customRowStyle,
  renderCellValue,
  rowOnClick,
}) => {
  const handleExpandButtonClick = () => {
    if (expandedRowIds.has(rowIndex)) {
      setExpandedRowIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowIndex);
        return newSet;
      });
    } else {
      setExpandedRowIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(rowIndex);
        return newSet;
      });
    }
  };
  const handleRowClick = () => {
    if (rowOnClick) {
      rowOnClick(row);
    }
  };
  const getNumericValue = (col) => {
    // Extract, strip '%', and safely default to an empty string if null/undefined
    const rawValue = row?.[col?.id]?.toString().replace('%', '') ?? '';

    // Parse the cleaned string, returning NaN if empty or invalid
    return parseFloat(rawValue);
  };

  //
  return (
    <>
      <TableRow
        key={row?.id || rowIndex}
        onClick={handleRowClick}
        sx={{ cursor: rowOnClick ? 'pointer' : 'default' }}
      >
        {flatColumns.map((col, index) => (
          <TableCell
            key={`${col.id}_${col.label}_${rowIndex}`}
            align={col.align || 'left'}
            sx={{
              ...ROW_STYLE,
              ...customRowStyle,
              bgcolor: rowIndex % 2 === 0 ? primaryRowColor : secondaryRowColor,
              color: getNumericValue(col) < 0 ? secondaryTextColor : primaryTextColor,
              fontWeight: expandedRowIds?.has(rowIndex) ? 'bold' : 'normal',
              cursor: rowOnClick && index === 0 ? 'pointer' : 'default',
              '&:hover':
                rowOnClick && index === 0
                  ? {
                      backgroundColor: '#cccccc',
                    }
                  : {},
            }}
          >
            {index === 0 && row?.children ? (
              <Box display="flex" alignItems="center">
                <Box sx={EXPAND_BUTTON_BOX_STYLE}>
                  <IconButton
                    aria-label="expand row"
                    size="small"
                    sx={EXPAND_BUTTON_STYLE}
                    onClick={handleExpandButtonClick}
                  >
                    {expandedRowIds?.has(rowIndex) ? (
                      <RemoveIcon sx={BUTTON_ICON_STYLE} />
                    ) : (
                      <AddIcon sx={BUTTON_ICON_STYLE} />
                    )}
                  </IconButton>
                </Box>
                {renderCellValue(row?.[col?.id], col?.type)}
              </Box>
            ) : (
              renderCellValue(row?.[col?.id], col?.type)
            )}
          </TableCell>
        ))}
      </TableRow>
      {row?.children &&
        expandedRowIds?.has(rowIndex) &&
        row?.children.map((childRow, childIndex) => (
          <TableRow key={childRow?.id || childIndex}>
            {flatColumns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align || 'left'}
                sx={{
                  ...ROW_STYLE,
                  ...customRowStyle,
                  paddingLeft: 5.5,
                  bgcolor: childIndex % 2 === 0 ? secondaryRowColor : primaryRowColor,
                  color: parseFloat(row?.[col?.id]) < 0 ? secondaryTextColor : primaryTextColor,
                }}
              >
                {renderCellValue(childRow?.[col?.id], col?.type)}
              </TableCell>
            ))}
          </TableRow>
        ))}
    </>
  );
};

export default BodyRow;
